import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { SpellBookBackend } from "./backend";

// Session context
const SessionCtx = createContext(null);
export const useSession = () => useContext(SessionCtx);

// In-memory access token (reduces XSS blast radius)
let _accessToken = null;

export const setAccessToken = (t) => { _accessToken = t };
export const clearAccessToken = () => { _accessToken = null };
export const getAccessToken = () => _accessToken;

export async function refreshAccessToken() {
    const token = await SpellBookBackend.refreshSession()
    setAccessToken(token);
    return token;
}

export async function authenticatedApiFetch(url, init = {}) {
    const withAuth = () => {
        const headers = { ...(init.headers || {}) };
        if (init.body && !("Content-Type" in headers) && !("content-type" in headers)) {
            headers["Content-Type"] = "application/json";
        };

        const accessToken = getAccessToken(); 
        if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

        return fetch(url, {
            ...init,
            credentials: "include",
            headers,
        });
    };

    let res = await withAuth();
    if (res.status === 401) {
        try {
            await refreshAccessToken();
            res = await withAuth();
        }
        catch {
          // still 401 or refresh failed; return original failure
        }
    }

    return res;
}

export function SessionProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(() => !!getAccessToken());
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const login = useCallback(async (username, password) => {
        setLoading(true);
        setError(null);
        try {
            const token = await SpellBookBackend.createSession(username, password);
            setAccessToken(token);
            setIsAuthenticated(true);
            setUser({ username });
            return true;
        }
        catch (e) {
            setError(e.message);
            setIsAuthenticated(false);
            clearAccessToken();
            return false;
        }
        finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            SpellBookBackend.deleteSession(getAccessToken())
        }
        catch (e) {
            // ignore network errors on logout
        }
        finally {
            clearAccessToken();
            setIsAuthenticated(false);
            setUser(null);
            setLoading(false);
        }
    }, []);

    // Silent restore on load: try to mint a token using the refresh cookie
    useEffect(() => {
        (
            async () => {
                const t = getAccessToken();
                if (!getAccessToken()) {
                    try {
                        const token = await refreshAccessToken();
                        if (token) setIsAuthenticated(true);
                    } catch {
                        // no refresh cookie or refresh failed
                    }
                }
            }
        )();
    }, []);

    const value = useMemo(
        () => ({ isAuthenticated, user, loading, error, login, logout }),
        [isAuthenticated, user, loading, error, login, logout]
    );

    return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}